// @ts-check
// Painel da conversa: carrega o thread e as mensagens, atualiza por polling a cada 10 s, marca como lida,
// envia com mensagem otimista (PENDING) e troca entre a visão de WhatsApp e a de e-mail.
import { html } from 'htm/preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { assignThread, getThread, linkThreadToPerson, listMessages, markThreadRead, sendMessage } from '../../data/conversations.js';
import { ApiError, getErrorMessage } from '../../data/api-client.js';
import { currentWorkspaceMemberState } from '../../data/metadata-store.js';
import { formatFullName } from '../../lib/field-metadata.js';
import { getAttachmentKindFromMimeType, formatServiceWindowExpiry } from '../../lib/conversation-format.js';
import { generateId } from '../../lib/utils.js';
import { Icon } from '../../ui/icon.js';
import { EmptyState, Loader } from '../../ui/feedback.js';
import { showErrorSnackbar, showSuccessSnackbar } from '../../ui/snackbar.js';
import { ConversationHeader } from './conversation-header.js';
import { ConversationOpportunityBanner } from './conversation-opportunity-banner.js';
import { WhatsappMessageList } from './whatsapp-message-list.js';
import { EmailThread } from './email-thread.js';
import { MessageComposer } from './message-composer.js';
import { getThreadDisplayName } from './conversation-display.js';
import { CONVERSATION_POLLING_INTERVAL_MILLISECONDS, refreshUnreadConversationCount, usePolling } from './conversations-state.js';
import { t } from '../../i18n/index.js';
import { Button } from '../../ui/button.js';
import { TemplateMessageModal } from './template-message-modal.js';
// faixa do bot que conduz a conversa (3.0.0, contrato de bots, seção 22.4); sem bot, nada
import { BotConversationBanner } from '../bots/bot-conversation-banner.js';

/**
 * @typedef {import('../../data/conversations.js').ConversationThread} ConversationThread
 * @typedef {import('../../data/conversations.js').ConversationMessage} ConversationMessage
 * @typedef {import('./message-composer.js').UploadedAttachment} UploadedAttachment
 */

const MESSAGE_PAGE_LIMIT = 50;

/**
 * Junta páginas de mensagens por id, mantendo as antigas já carregadas, em ordem cronológica.
 * @param {ConversationMessage[]} current
 * @param {ConversationMessage[]} incoming
 */
function mergeMessages(current, incoming) {
  const byId = new Map(current.map((message) => [message.id, message]));
  for (const message of incoming) byId.set(message.id, message);
  return [...byId.values()].sort((first, second) => first.sentAt.localeCompare(second.sentAt));
}

/**
 * @param {{ thread: ConversationThread, onSendTemplate?: () => void }} props
 */
function ServiceWindowBanner({ thread, onSendTemplate }) {
  if (thread.channelType !== 'WHATSAPP') return null;
  if (thread.isServiceWindowOpen && thread.serviceWindowExpiresAt) {
    return html`<div class="service-window-banner" data-state="open">
      <${Icon} name="IconClock" size=${14} />
      <span>${formatServiceWindowExpiry(thread.serviceWindowExpiresAt)}</span>
    </div>`;
  }
  return html`<div class="service-window-banner" data-state="closed" role="status">
    <${Icon} name="IconAlertTriangle" size=${16} />
    <span>${t('conversations.window.closed')}</span>
    ${onSendTemplate ? html`<${Button} title=${t('leadCapture.templateMessage.open')} icon="IconSend" accent="blue" size="small" onClick=${onSendTemplate} />` : null}
  </div>`;
}

/**
 * @param {{ threadId: string, onThreadChange?: (thread: ConversationThread) => void, onBack?: () => void }} props
 */
export function ConversationPane({ threadId, onThreadChange, onBack }) {
  const [thread, setThread] = useState(/** @type {ConversationThread | null} */ (null));
  const [messages, setMessages] = useState(/** @type {ConversationMessage[]} */ ([]));
  const [pendingMessages, setPendingMessages] = useState(/** @type {ConversationMessage[]} */ ([]));
  const [status, setStatus] = useState(/** @type {'loading' | 'ready' | 'notFound'} */ ('loading'));
  const [olderCursor, setOlderCursor] = useState(/** @type {string | null} */ (null));
  const [isLoadingOlder, setIsLoadingOlder] = useState(false);
  // modelo aprovado do WhatsApp, para a janela de 24 horas fechada (contrato 08)
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const onThreadChangeRef = useRef(onThreadChange);
  onThreadChangeRef.current = onThreadChange;
  const isMarkingReadRef = useRef(false);

  /** @param {ConversationThread} nextThread */
  const applyThread = (nextThread) => {
    setThread(nextThread);
    onThreadChangeRef.current?.(nextThread);
  };

  /** @param {ConversationThread} loadedThread */
  const markReadIfNeeded = (loadedThread) => {
    if (loadedThread.unreadCount === 0 || isMarkingReadRef.current || document.visibilityState !== 'visible') return;
    isMarkingReadRef.current = true;
    markThreadRead(loadedThread.id)
      .then((updatedThread) => {
        applyThread(updatedThread?.id ? updatedThread : { ...loadedThread, unreadCount: 0 });
        refreshUnreadConversationCount();
      })
      .catch(() => {
        // Continua não lida; o próximo ciclo de polling tenta de novo.
      })
      .finally(() => {
        isMarkingReadRef.current = false;
      });
  };

  /** @param {{ isInitial?: boolean }} [options] */
  const load = async ({ isInitial = false } = {}) => {
    try {
      const [loadedThread, page] = await Promise.all([getThread(threadId), listMessages(threadId, { limit: MESSAGE_PAGE_LIMIT })]);
      if (!loadedThread) {
        setStatus('notFound');
        return;
      }
      applyThread(loadedThread);
      setMessages((current) => (isInitial ? page.messages : mergeMessages(current, page.messages)));
      if (isInitial) setOlderCursor(page.pageInfo.hasPreviousPage ? page.pageInfo.startCursor ?? page.messages[0]?.id ?? null : null);
      setStatus('ready');
      markReadIfNeeded(loadedThread);
    } catch (error) {
      // a conversa aberta some quando o lead vai para uma etapa que o papel não vê: a próxima leitura dá 404
      if (error instanceof ApiError && error.statusCode === 404) {
        setStatus('notFound');
        return;
      }
      if (!isInitial) return;
      if (error instanceof ApiError && error.statusCode === 404) setStatus('notFound');
      else {
        showErrorSnackbar(error);
        setStatus('notFound');
      }
    }
  };

  useEffect(() => {
    setThread(null);
    setMessages([]);
    setPendingMessages([]);
    setOlderCursor(null);
    setStatus('loading');
    load({ isInitial: true });
  }, [threadId]);

  usePolling(() => load(), CONVERSATION_POLLING_INTERVAL_MILLISECONDS, status === 'ready');

  const loadOlder = async () => {
    if (!olderCursor) return;
    setIsLoadingOlder(true);
    try {
      const page = await listMessages(threadId, { limit: MESSAGE_PAGE_LIMIT, startingBefore: olderCursor });
      setMessages((current) => mergeMessages(current, page.messages));
      setOlderCursor(page.pageInfo.hasPreviousPage ? page.pageInfo.startCursor ?? page.messages[0]?.id ?? null : null);
    } catch (error) {
      showErrorSnackbar(error);
    } finally {
      setIsLoadingOlder(false);
    }
  };

  /**
   * Mensagem otimista com o relógio (PENDING) até a API responder.
   * @param {{ text: string, subject?: string, attachments: UploadedAttachment[] }} input
   */
  const send = async ({ text, subject, attachments }) => {
    if (!thread) return;
    const member = currentWorkspaceMemberState.value;
    const optimisticId = `optimistic-${generateId()}`;
    /** @type {ConversationMessage} */
    const optimistic = {
      id: optimisticId,
      threadId: thread.id,
      direction: 'OUTGOING',
      status: 'PENDING',
      sentAt: new Date().toISOString(),
      subject: subject ?? null,
      text: text || null,
      html: null,
      from: { handle: '', displayName: formatFullName(member?.name) },
      to: [{ handle: thread.contact.handle, displayName: thread.contact.displayName, role: 'TO' }],
      attachments: attachments.map((attachment) => ({
        fileId: attachment.fileId,
        fileName: attachment.fileName,
        mimeType: attachment.mimeType,
        size: attachment.size,
        kind: getAttachmentKindFromMimeType(attachment.mimeType),
        isVoiceNote: false,
        url: attachment.localUrl,
      })),
      sentBy: member ? { workspaceMemberId: member.id, name: formatFullName(member.name) } : null,
      isOptimistic: true,
    };
    setPendingMessages((current) => [...current, optimistic]);
    try {
      const message = await sendMessage(thread.id, {
        text,
        ...(attachments.length ? { fileIds: attachments.map((attachment) => attachment.fileId) } : {}),
        ...(thread.channelType === 'EMAIL' && subject ? { subject } : {}),
      });
      setPendingMessages((current) => current.filter((candidate) => candidate.id !== optimisticId));
      if (message?.id) setMessages((current) => mergeMessages(current, [message]));
      load();
    } catch (error) {
      setPendingMessages((current) =>
        current.map((candidate) => (candidate.id === optimisticId ? { ...candidate, status: 'FAILED', errorMessage: getErrorMessage(error) } : candidate)),
      );
      if (error instanceof ApiError && error.code === 'SERVICE_WINDOW_CLOSED') load();
      else showErrorSnackbar(error);
    }
  };

  /** @param {string | null} personId */
  const linkPerson = async (personId) => {
    if (!thread) return;
    try {
      const updatedThread = await linkThreadToPerson(thread.id, personId);
      applyThread(updatedThread?.id ? updatedThread : await getThread(thread.id));
      showSuccessSnackbar(t('conversations.linkedSnackbar'));
    } catch (error) {
      showErrorSnackbar(error);
    }
  };

  /** @param {string | null} workspaceMemberId */
  const assign = async (workspaceMemberId) => {
    if (!thread) return;
    try {
      const updatedThread = await assignThread(thread.id, workspaceMemberId);
      const nextThread = updatedThread?.id ? updatedThread : await getThread(thread.id);
      applyThread(nextThread);
      showSuccessSnackbar(
        nextThread.assignee ? t('conversations.assignee.assigned', { name: nextThread.assignee.name }) : t('conversations.assignee.unassigned'),
      );
    } catch (error) {
      showErrorSnackbar(error);
    }
  };

  if (status === 'notFound') {
    // a conversa deixou de existir ou o lead foi para uma etapa que o papel não vê (contrato de equipe, seção 3.2)
    return html`<div class="conversation-pane conversation-pane-empty" data-testid="conversation-unavailable">
      <${EmptyState} icon="IconMessages" title=${t('conversations.unavailableTitle')} subtitle=${t('conversations.unavailable')} />
    </div>`;
  }
  if (!thread) {
    return html`<div class="conversation-pane conversation-pane-empty"><${Loader} /></div>`;
  }

  const allMessages = [...messages, ...pendingMessages];
  const isWindowClosed = thread.channelType === 'WHATSAPP' && !thread.isServiceWindowOpen;
  return html`<div class="conversation-pane" data-channel=${thread.channelType}>
    <${ConversationHeader} thread=${thread} onLinkPerson=${linkPerson} onAssign=${assign} onBack=${onBack} />
    <${ConversationOpportunityBanner} thread=${thread} onThreadChange=${applyThread} />
    <${BotConversationBanner} thread=${thread} onChanged=${load} />
    ${thread.isBlocked
      ? html`<div class="service-window-banner" data-state="closed" role="status" data-testid="blocked-thread-banner">
          <${Icon} name="IconForbid" size=${16} /><span>${t('emailAdvanced.conversation.blocked')}</span>
        </div>`
      : null}
    ${thread.channelType === 'EMAIL'
      ? html`<${EmailThread}
          thread=${thread}
          messages=${allMessages}
          hasOlder=${Boolean(olderCursor)}
          isLoadingOlder=${isLoadingOlder}
          onLoadOlder=${loadOlder}
          onSendReply=${send}
        />`
      : html`
          <${WhatsappMessageList}
            messages=${allMessages}
            contactName=${getThreadDisplayName(thread)}
            hasOlder=${Boolean(olderCursor)}
            isLoadingOlder=${isLoadingOlder}
            onLoadOlder=${loadOlder}
          />
          <${ServiceWindowBanner} thread=${thread} onSendTemplate=${() => setIsTemplateModalOpen(true)} />
          ${isTemplateModalOpen ? html`<${TemplateMessageModal} thread=${thread} onClose=${() => setIsTemplateModalOpen(false)} onSent=${() => {
            setIsTemplateModalOpen(false);
            load();
          }} />` : null}
          <${MessageComposer}
            key=${thread.id}
            isDisabled=${isWindowClosed}
            placeholder=${isWindowClosed ? t('conversations.composer.placeholderClosed') : undefined}
            onSend=${send}
          />
        `}
  </div>`;
}
